import get from 'lodash/get';
import { func, shape, string } from 'prop-types';
import { useForm } from 'react-hook-form';
import { Compare } from '@fff-web/fff-utilities';
import {
  addressMessages,
  addressStatus,
  addressSuggestionStatus,
  countryCodeIds,
  errorMessages as em,
} from '../../constants/address';
import useStatesList from '../../hooks/useStatesList';
import { verifyAddress } from '../../services/localization';
import './styles.scss';
import { getAddressNoSuggestionMessage } from '../../helpers/address';

const EditAddressForm = ({ onCancel = () => {}, onSubmit = () => {}, address = '' }) => {
  const initialCountry = get(address, 'countryCode');
  const { statesList, setCountryId } = useStatesList(get(countryCodeIds, initialCountry));
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: address,
  });

  const handleHandleSubmit = (values) => {
    verifyAddress(values)
      .then((r) => {
        const { deliveryStatus: status, submitted, verified } = r;
        const response = { ...r, readyToSubmit: false };

        switch (status) {
          case addressStatus.UNDELIVERABLE_STATUS:
          case addressStatus.NO_MATCH_STATUS:
            // no suggestion available, can edit address
            response.modalType = addressSuggestionStatus.NO_SUGGESTION;
            response.message = addressMessages.UNDELIVERABLE_MSG;
            break;

          case addressStatus.DELIVERABLE_STATUS:
            if (Compare.isEqualObject(submitted, verified)) {
              // suggestion available,can edit address
              response.modalType = addressSuggestionStatus.SUGGESTION_AVAILABLE;
            } else {
              // address is verified as user entered
              // submit address
              response.modalType = addressSuggestionStatus.VERIFIED;
              response.readyToSubmit = true;
            }
            break;

          default:
            // show single address with message
            response.modalType = addressSuggestionStatus.NO_SUGGESTION;
            response.message = getAddressNoSuggestionMessage(status);
            response.readyToSubmit = true;
            break;
        }

        onSubmit(response);
        return response;
      })
      .catch(console.error);
  };

  const handleSelectCountry = async ({ target: { value } }) => {
    await setCountryId(get(countryCodeIds, value));
    if (initialCountry === value) {
      setValue('state', get(address, 'region'));
    } else {
      setValue('state', '');
    }
  };

  return (
    <form
      className="edit-address-form"
      onSubmit={handleSubmit(handleHandleSubmit)}
      data-testid="edit-address-form"
    >
      <label htmlFor="countryCode" className="with-chevron" aria-label="label">
        <span>Country / Region</span>
        <select
          {...register('countryCode', { required: em.require })}
          onChange={handleSelectCountry}
        >
          <option value="">Select Country</option>
          <option value="CA">Canada</option>
          <option value="GB">United Kingdom</option>
          <option value="US">United States</option>
        </select>
        {get(errors.countryCode, 'message')}
      </label>
      <div className="address-fields">
        <label htmlFor="primaryLine" aria-label="label">
          <span>Street Address</span>
          <input
            {...register('primaryLine', { required: em.require })}
            placeholder="360 N. La Cienga"
          />
          {get(errors.primaryLine, 'message')}
        </label>
        <label htmlFor="secondaryLine" aria-label="label">
          <span>Apt, Suite, Room</span>
          <input {...register('secondaryLine')} />
        </label>
      </div>
      <label htmlFor="city" aria-label="label">
        <span>City</span>
        <input {...register('city', { required: em.require })} placeholder="Beverly Hills" />
        {get(errors.city, 'message')}
      </label>
      <div className="state-fields">
        <label htmlFor="state" className="with-chevron" aria-label="label">
          <span>State</span>
          <select
            {...register('state', { required: statesList.length > 1 && em.require })}
            disabled={statesList.length < 2}
            placeholder="Select state"
            key={statesList.length && initialCountry}
          >
            <option value="">Select state</option>
            {statesList.map(({ stateCode, displayName }) => (
              <option key={stateCode} value={stateCode}>
                {displayName}
              </option>
            ))}
          </select>
          {get(errors.state, 'message')}
        </label>
        <label htmlFor="zip" aria-label="label">
          <span>Zip Code</span>
          <input {...register('zip', { required: em.require })} placeholder="90210" />
          {get(errors.zip, 'message')}
        </label>
      </div>
      <div className="buttons">
        <button type="button" className="fff-btn-primary-black-outline" onClick={onCancel}>
          CANCEL
        </button>
        <button type="submit" className="fffBtnPrimary" disabled={!statesList.length}>
          UPDATE
        </button>
      </div>
    </form>
  );
};

EditAddressForm.propTypes = {
  title: string,
  subtitle: string,
  address: shape({
    address1: string,
    address2: string,
    city: string,
    region: string,
    code: string,
  }),
  onSubmit: func,
  onCancel: func,
};

export default EditAddressForm;
